import React, { useState, useEffect, useMemo } from 'react'
import { Modal, Button, Form, Spinner, Alert, Table, Badge } from 'react-bootstrap'
import { recipeAPI } from '../api/recipeAPI'

const EXPIRATION_WARNING_DAYS = 90

function PickInventoryModal({
    show,
    onHide,
    ingredient,
    requiredQuantity,
    recipeId,
    onConfirm,
    existingAllocations
}) {
    const [inventoryItems, setInventoryItems] = useState([])
    const [allocations, setAllocations] = useState({})
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState(null)

    // Fetch inventory when modal opens
    useEffect(() => {
        if (show && ingredient && recipeId) {
            fetchInventory()
        }
    }, [show, ingredient, recipeId])

    // Initialize allocations from existing when modal opens
    useEffect(() => {
        if (show && existingAllocations) {
            setAllocations(existingAllocations)
        } else if (show) {
            setAllocations({})
        }
    }, [show, existingAllocations])

    const fetchInventory = async () => {
        try {
            setLoading(true)
            setError(null)
            const data = await recipeAPI.getIngredientInventory(recipeId, ingredient._id)
            setInventoryItems(data || [])
        } catch (err) {
            console.error('Error fetching inventory:', err)
            setError('Failed to load available inventory')
        } finally {
            setLoading(false)
        }
    }

    // Calculate totals
    const totalPicked = useMemo(() => {
        return Object.values(allocations).reduce((sum, qty) => sum + (parseFloat(qty) || 0), 0)
    }, [allocations])

    const totalAvailable = useMemo(() => {
        return inventoryItems.reduce((sum, item) => sum + parseFloat(item.quantity), 0)
    }, [inventoryItems])

    const difference = totalPicked - requiredQuantity

    // Check if item is expired or expiring soon
    const getExpirationStatus = (bbDate) => {
        if (!bbDate) return null
        const today = new Date()
        today.setHours(0, 0, 0, 0)
        const expDate = new Date(bbDate)
        expDate.setHours(0, 0, 0, 0)

        const daysUntilExpiration = Math.ceil((expDate - today) / (1000 * 60 * 60 * 24))

        if (daysUntilExpiration < 0) return 'expired'
        if (daysUntilExpiration <= EXPIRATION_WARNING_DAYS) return 'warning'
        return null
    }

    const handleQuantityChange = (inventoryId, value) => {
        const numValue = parseFloat(value) || 0
        const item = inventoryItems.find(i => i._id === inventoryId)
        const maxQty = item ? parseFloat(item.quantity) : 0

        if (numValue <= 0) {
            // Remove allocation if zero or negative
            const newAllocations = { ...allocations }
            delete newAllocations[inventoryId]
            setAllocations(newAllocations)
        } else {
            setAllocations({
                ...allocations,
                [inventoryId]: Math.min(numValue, maxQty)
            })
        }
    }

    const handleAutoPick = () => {
        let remaining = requiredQuantity
        const newAllocations = {}

        for (const item of inventoryItems) {
            if (remaining <= 0) break
            const available = parseFloat(item.quantity)
            const pickQty = Math.min(remaining, available)
            if (pickQty > 0) {
                newAllocations[item._id] = pickQty
                remaining -= pickQty
            }
        }

        setAllocations(newAllocations)
    }

    const handleClear = () => {
        setAllocations({})
    }

    const handleConfirm = () => {
        // Filter out zero allocations and create final allocation object
        const finalAllocations = {}
        for (const [id, qty] of Object.entries(allocations)) {
            if (qty > 0) {
                finalAllocations[id] = qty
            }
        }
        onConfirm(finalAllocations)
        onHide()
    }

    const formatDate = (dateStr) => {
        if (!dateStr) return '-'
        return new Date(dateStr).toLocaleDateString()
    }

    const ingredientName = ingredient?.ingredient_name ||
                          ingredient?.stock_item_name ||
                          ingredient?.item_family_name ||
                          'Unknown Ingredient'

    return (
        <Modal show={show} onHide={onHide} size="xl">
            <Modal.Header closeButton>
                <Modal.Title>Pick Inventory - {ingredientName}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {/* Summary Bar */}
                <div className="d-flex justify-content-between align-items-center mb-3 p-3 bg-light rounded">
                    <div>
                        <strong>Required:</strong> {requiredQuantity.toFixed(4)}
                    </div>
                    <div>
                        <strong>Picked:</strong> {totalPicked.toFixed(4)}
                    </div>
                    <div>
                        {difference === 0 ? (
                            <Badge bg="success">Exact Match</Badge>
                        ) : difference > 0 ? (
                            <Badge bg="warning" text="dark">Over by {difference.toFixed(4)}</Badge>
                        ) : (
                            <Badge bg="danger">Short by {Math.abs(difference).toFixed(4)}</Badge>
                        )}
                    </div>
                    <div>
                        <strong>Total Available:</strong> {totalAvailable.toFixed(4)}
                    </div>
                </div>

                {/* Warning Messages */}
                {totalAvailable < requiredQuantity && (
                    <Alert variant="warning" className="mb-3">
                        Insufficient inventory: Only {totalAvailable.toFixed(4)} available, but {requiredQuantity.toFixed(4)} required.
                    </Alert>
                )}

                {/* Action Buttons */}
                <div className="mb-3 d-flex justify-content-end">
                    <Button
                        variant="outline-secondary"
                        size="sm"
                        className="me-2"
                        onClick={handleClear}
                        disabled={loading || Object.keys(allocations).length === 0}
                    >
                        Clear
                    </Button>
                    <Button
                        variant="primary"
                        size="sm"
                        onClick={handleAutoPick}
                        disabled={loading || inventoryItems.length === 0}
                    >
                        Auto Pick
                    </Button>
                </div>

                {/* Inventory Table */}
                {loading ? (
                    <div className="text-center py-4">
                        <Spinner animation="border" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </Spinner>
                    </div>
                ) : error ? (
                    <Alert variant="danger">{error}</Alert>
                ) : inventoryItems.length === 0 ? (
                    <Alert variant="info">No inventory available for this ingredient.</Alert>
                ) : (
                    <div className="table-responsive" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                        <Table striped bordered hover size="sm">
                            <thead className="sticky-top bg-white">
                                <tr>
                                    {ingredient?.item_family && <th>Stock Item</th>}
                                    <th>LOT</th>
                                    <th>Best Before</th>
                                    <th>Available</th>
                                    <th style={{ width: '120px' }}>Pick Qty</th>
                                    <th>Location</th>
                                    <th>Vendor</th>
                                </tr>
                            </thead>
                            <tbody>
                                {inventoryItems.map((item) => {
                                    const expStatus = getExpirationStatus(item.bb_date)
                                    const rowClass = expStatus === 'expired' ? 'table-danger' :
                                                    expStatus === 'warning' ? 'table-warning' : ''
                                    return (
                                        <tr key={item._id} className={rowClass}>
                                            {ingredient?.item_family && (
                                                <td>{item.stock_item?.description || '-'}</td>
                                            )}
                                            <td>{item.lot || '-'}</td>
                                            <td>
                                                {formatDate(item.bb_date)}
                                                {expStatus === 'expired' && (
                                                    <Badge bg="danger" className="ms-1">Expired</Badge>
                                                )}
                                            </td>
                                            <td>{parseFloat(item.quantity).toFixed(4)}</td>
                                            <td>
                                                <Form.Control
                                                    type="number"
                                                    size="sm"
                                                    min="0"
                                                    max={item.quantity}
                                                    step="0.0001"
                                                    value={allocations[item._id] || ''}
                                                    onChange={(e) => handleQuantityChange(item._id, e.target.value)}
                                                    placeholder="0"
                                                />
                                            </td>
                                            <td>{item.warehouse_name} / {item.location_name}</td>
                                            <td>{item.vendor_name || '-'}</td>
                                        </tr>
                                    )
                                })}
                            </tbody>
                        </Table>
                    </div>
                )}
            </Modal.Body>
            <Modal.Footer>
                {difference !== 0 && totalPicked > 0 && (
                    <Alert variant={difference > 0 ? 'warning' : 'danger'} className="mb-0 me-auto py-1 px-2">
                        {difference > 0
                            ? `Warning: Picking ${difference.toFixed(4)} more than required`
                            : `Warning: Picking ${Math.abs(difference).toFixed(4)} less than required`
                        }
                    </Alert>
                )}
                <Button variant="secondary" onClick={onHide}>
                    Cancel
                </Button>
                <Button
                    variant="success"
                    onClick={handleConfirm}
                    disabled={loading}
                >
                    Confirm
                </Button>
            </Modal.Footer>
        </Modal>
    )
}

export default PickInventoryModal
